"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { Sun, Moon, Monitor, Check } from "lucide-react";
import { cn } from "@/lib/utils";

// Three-way theme switcher — Light / System / Dark. Uses a dropdown so the
// icon-only trigger is compact in tight navs (public top bar, admin sidebar
// footer). "System" tracks prefers-color-scheme live; changing OS theme
// updates the UI without reload.
//
// `variant="icon"` (default) is a square icon button for the public nav.
// `variant="full"` is a wider labeled button for the admin sidebar where
// there's horizontal space.
export function ThemeToggle({
  variant = "icon",
  className,
}: {
  variant?: "icon" | "full";
  className?: string;
}) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // next-themes relies on window APIs; server render shows a neutral
  // placeholder so we don't trigger hydration mismatch warnings on initial
  // load when the class hasn't been applied yet.
  useEffect(() => setMounted(true), []);

  // Close on outside click / Escape — small UX polish expected of any
  // dropdown that doesn't use a Portal.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Which icon to display in the trigger. For "system" we show the resolved
  // theme icon so the user sees what they're currently getting.
  const TriggerIcon = !mounted
    ? Monitor
    : theme === "system"
    ? (resolvedTheme === "dark" ? Moon : Sun)
    : theme === "dark"
    ? Moon
    : Sun;

  const options = [
    { value: "light", label: "Light", Icon: Sun },
    { value: "system", label: "System", Icon: Monitor },
    { value: "dark", label: "Dark", Icon: Moon },
  ] as const;

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        aria-label="Toggle theme"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg border border-border bg-background hover:bg-muted transition-colors",
          variant === "icon" ? "h-9 w-9 justify-center" : "h-9 px-3 text-sm",
        )}
      >
        <TriggerIcon size={15} />
        {variant === "full" && (
          <span className="capitalize">{mounted ? (theme ?? "system") : "theme"}</span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-40 rounded-lg border border-border bg-background shadow-lg py-1"
        >
          {options.map(({ value, label, Icon }) => {
            const selected = mounted && theme === value;
            return (
              <button
                key={value}
                role="menuitem"
                onClick={() => { setTheme(value); setOpen(false); }}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors",
                  selected && "font-semibold",
                )}
              >
                <Icon size={14} />
                <span className="flex-1 text-left">{label}</span>
                {selected && <Check size={13} className="text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
